// Imported images keep their original transform baked into their pixels.
// Replacements bake that same transform once, without changing the node frame.
export function artworkTransform(node, originals = []) {
  if (node.artworkTransform) return node.artworkTransform;
  const original = originals.find((n) => n.src === node.src && n.artworkTransform);
  if (original) return original.artworkTransform;
  if (["Original corner ornament", "Original flourish"].includes(node.name))
    throw new Error("Could not recover this ornament’s original orientation. The artwork has not been changed.");
  return [1, 0, 0, 1];
}

// Section ornaments keep their left/right orientation, but an uploaded image
// must not inherit the old flourish's baked ten-degree slant.
export function replacementArtworkMatrix(node, recovered) {
  if (node.headingWinglet) return [node.headingWinglet.side === "right" ? -1 : 1, 0, 0, 1];
  return node.artworkTransform || recovered || [1, 0, 0, 1];
}

// Earlier replacements already have that slant baked into their pixels. Undo
// only the inherited rotation at render time; manual node rotation still works.
export function inheritedHeadingTiltCorrection(node) {
  if (!node.headingWinglet?.customArtwork || !node.artworkTransform) return 0;
  const [a, b] = node.artworkTransform;
  const facing = node.headingWinglet.side === "right" ? -1 : 1;
  const degrees = Math.atan2(b * facing, a * facing) * 180 / Math.PI;
  return Math.abs(degrees) < 0.001 ? 0 : -degrees;
}

async function artworkSample(src) {
  const img = new Image();
  img.src = src;
  await img.decode();
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, 64, 64);
  const pixels = ctx.getImageData(0, 0, 64, 64).data;
  // Composite against white so transparent and white-backed imports compare alike.
  return Array.from({ length: 64 * 64 }, (_, i) => {
    const offset = i * 4;
    return pixels[offset + 3] / 255 * (1 - (pixels[offset] + pixels[offset + 1] + pixels[offset + 2]) / 765);
  });
}

export async function recoverArtworkTransforms(nodes, originals, sample = artworkSample) {
  const samples = new Map(), recovered = new Map();
  const read = (src) => {
    if (!samples.has(src)) samples.set(src, sample(src));
    return samples.get(src);
  };
  for (const node of nodes) {
    if (recovered.has(node.src) || node.artworkTransform) continue;
    try {
      recovered.set(node.src, artworkTransform(node, originals));
      continue;
    } catch (_) { /* Legacy raster bytes can vary between browser renderings. */ }
    const reference = await read(node.src);
    const candidates = Array.from(new Map(originals.filter((n) =>
      n.type === "image" && n.name === node.name && n.artworkTransform
    ).map((n) => [n.src, n])).values());
    let best, bestScore = Infinity;
    for (const candidate of candidates) {
      const pixels = await read(candidate.src);
      let difference = 0, ink = 0;
      reference.forEach((value, i) => {
        difference += Math.abs(value - pixels[i]);
        ink += Math.max(value, pixels[i]);
      });
      const score = ink ? difference / ink : Infinity;
      if (score < bestScore) { bestScore = score; best = candidate; }
    }
    if (!best || bestScore > 0.35)
      throw new Error("Could not identify this ornament’s orientation. The artwork has not been changed.");
    recovered.set(node.src, best.artworkTransform);
  }
  return recovered;
}

export function transformArtwork(img, matrix, maxPixels = 2400) {
  const [a, b, c, d] = matrix;
  const w = img.naturalWidth, h = img.naturalHeight;
  const width = Math.abs(a) * w + Math.abs(c) * h;
  const height = Math.abs(b) * w + Math.abs(d) * h;
  const scale = Math.min(1, Math.max(1, maxPixels) / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.ceil(width * scale));
  canvas.height = Math.max(1, Math.ceil(height * scale));
  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.scale(scale, scale);
  ctx.transform(a, b, c, d, 0, 0);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  return { src: canvas.toDataURL("image/png"), sourceWidth: canvas.width, sourceHeight: canvas.height };
}

export function replaceArtwork(doc, replacements) {
  return { ...doc, pages: doc.pages.map((p) => ({ ...p, elements: p.elements.map((n) =>
    n.type === "image" && !n.locked && replacements[n.id] ? { ...n, ...replacements[n.id] } : n
  ) })) };
}
